import { Button, Spinner } from '@nike/eds';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SET_NPOS_FILTERS, setNposErrors, setNposValue } from '../../../redux/actions';
import {
 getBodyTranslations, getNposErrors, getNposFilters
} from '../../../redux/selectors';

import MoreFilters from './filters/MoreFilters';
import PaymentType from './filters/PaymentType';
import PurchaseDate from './filters/PurchaseDate';
import StoreFilter from './filters/StoreFilter';
import UPC from './filters/UPC';
import { getDefaultStateForMoreFilters, isAnyFilterApplied, isAnyFilterError } from './nposUtils';

const MORE_FILTERS = getDefaultStateForMoreFilters();

function NposFilters({
  onSubmit,
  onClear,
  isLoading,
  selectStore,
  selectCountry,
  handleChange,
  timezone,
  loadLatestTransactions,
  fields,
  storeLocale
}) {
  const [dateFilterOpen, setDateFilterOpen] = useState(false);
  const [resetSearch, setResetSearch] = useState(false);
  const dispatch = useDispatch();

  const nposFilters = useSelector(getNposFilters);
  const nposErrors = useSelector(getNposErrors);
  const filtersApplied = useSelector((state) => state.nposFilters);
  const translations = useSelector(getBodyTranslations);

  const showResetFiltersBtn = () => !isLoading && isAnyFilterApplied(filtersApplied);

  const onResetSearch = () => {
    onClear();
    dispatch(setNposErrors({}, true));
    setResetSearch(!resetSearch);
    dispatch({
      filter: {
        cardNo: { creditDebitCardNo: '', giftCardNo: '' },
        selectedDates: null,
        tenderType: null,
        upc: null,
        ...MORE_FILTERS
      },
      type: SET_NPOS_FILTERS,
    });
    dispatch(setNposValue({ currentPage: 1 }));
    loadLatestTransactions();
  };

  const onClickSearch = () => {
    if (!nposFilters.selectedDates) return setDateFilterOpen(true);

    dispatch(setNposValue({ currentPage: 1, isHomePage: false }));

    return onSubmit();
  };

  return (
    <div className="pt6-sm pb2-sm">
      <div
        className="pb4-sm"
        style={{ display: 'flex', gap: '10px', justifyContent: 'flex-start' }}
      >
        <StoreFilter selectCountry={selectCountry} selectStore={selectStore} />
      </div>
      <div
        className="d-sm-flx flx-jc-sm-fs flx-ai-sm-c"
        id="npos-filter-section"
        style={{
          gap: '10px'
        }}
      >
        <div
          className="pt2-sm d-sm-flx flx-jc-sm-fs flx-ai-sm-c"
          style={{
          gap: '10px'
        }}
        >
          <PurchaseDate
            handleChange={handleChange}
            open={dateFilterOpen}
            resetSearch={resetSearch}
            setOpen={setDateFilterOpen}
            timezone={timezone}
          />
          <PaymentType
            fields={fields}
            handleChange={handleChange}
            storeLocale={storeLocale}
          />
          <UPC
            handleChange={handleChange}
          />
          <MoreFilters handleChange={handleChange} />
        </div>
        <div
          style={{
            display: 'flex',
            flexGrow: '1',
            gap: '10px',
            justifyContent: 'flex-end',
          }}
        >
          {showResetFiltersBtn() && (
            <Button disableRipple size="medium" variant="ghost" onClick={onResetSearch}>
              {translations.RL_ResetSearch}
            </Button>
          )}
          <Button
            disableRipple
            data-testid="npos-search-button"
            disabled={isLoading || isAnyFilterError(nposErrors)}
            size="medium"
            style={{ cursor: isLoading ? 'default' : 'pointer', width: '8rem' }}
            variant="primary"
            onClick={isLoading ? null : onClickSearch}
          >
            {!isLoading ? (
              translations.RL_Search
            ) : (
              <div className="eds--dark">
                <Spinner size="medium" />
              </div>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

NposFilters.propTypes = {
  fields: PropTypes.shape().isRequired,
  handleChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  loadLatestTransactions: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  selectCountry: PropTypes.func.isRequired,
  selectStore: PropTypes.func.isRequired,
  storeLocale: PropTypes.string.isRequired,
  timezone: PropTypes.string.isRequired,
};

export default NposFilters;
